import type { ImageCategory, ImageOcrStatus, SourceType } from '../constants.js';
import type { CropMetadata, OcrConfidence } from '../types.js';
import type { DraftField, PackageDraftReview } from '../aggregation/types.js';

export const PACKAGE_SAVE_STATUSES = [
  'validating',
  'creating-strain',
  'uploading-images',
  'persisting-sources',
  'complete',
  'partial-failure',
  'failed',
] as const;

export type PackageSaveStatus = (typeof PACKAGE_SAVE_STATUSES)[number];

export const STRAIN_SAVE_STATUSES = ['complete', 'incomplete'] as const;
export type StrainSaveStatus = (typeof STRAIN_SAVE_STATUSES)[number];

export const FIELD_SOURCE_TYPES = ['manual', 'ocr-candidate'] as const;
export type FieldSourceType = (typeof FIELD_SOURCE_TYPES)[number];

/** Final strain values derived from PackageDraftReview (Phase 3 truth). */
export interface MappedStrainValues {
  name?: string;
  productName?: string;
  producer?: string;
  brand?: string;
  category?: string;
  cultivarType?: string;
  thcPercent: number;
  cbdPercent?: number;
  totalTerpenesPercent?: number;
  packageDate?: string;
  purchaseDate?: string;
  lotNumber?: string;
  sourceType: SourceType;
  notes?: string;
}

export interface FieldProvenanceRecord {
  fieldName: DraftField;
  finalValue: string | number;
  sourceType: FieldSourceType;
  candidateId?: string;
  clientImageId?: string;
  imageCategory?: ImageCategory;
  cropMetadata?: CropMetadata | null;
  rawSourceText?: string;
  sourceUnit?: string;
  normalizedValue?: string | number;
  ocrConfidence?: number | null;
  parserConfidence?: OcrConfidence;
  warnings: string[];
  confirmedAt?: string;
}

export interface CommitImageDescriptor {
  clientImageId: string;
  category: ImageCategory;
  sortOrder: number;
  isPrimary: boolean;
  originalFilename?: string | null;
  cropMetadata?: CropMetadata | null;
  ocrText?: string | null;
  ocrStatus?: ImageOcrStatus;
  scanMode?: string;
  strategy?: string;
}

/**
 * JSON body for POST /api/package-drafts/commit (images uploaded separately).
 */
export interface PackageDraftCommitRequest {
  clientOperationId: string;
  mode: 'package' | 'sticky_note';
  /** Full Phase 3 review — server maps values; does not trust client strain blob. */
  review: PackageDraftReview;
  images: CommitImageDescriptor[];
}

export interface PackageSaveImageState {
  clientImageId: string;
  sortOrder: number;
  category: ImageCategory;
  isPrimary: boolean;
  status: 'pending' | 'uploaded' | 'failed';
  imageId?: string | null;
  errorMessage?: string | null;
}

export interface PackageDraftCommitResult {
  sessionId: string;
  strainId: string;
  status: PackageSaveStatus;
  isVerified: boolean;
  strainSaveStatus: StrainSaveStatus;
  images: PackageSaveImageState[];
  warnings: string[];
  /** Present when status is complete. */
  strain?: unknown;
}
